import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { useApi } from '../lib/useApi';
import { getApiUrl } from '../lib/getApiUrl';
import ResponsiveContainer from '../components/layout/ResponsiveContainer';
import SharedHeaderCustomer from '../components/SharedHeaderCustomer';
import AgreementViewer from '../components/AgreementViewer';
import { ReviewModal } from '../components/ReviewModal'; // CB:BEGIN homeowner-reviews
import PostAwardSection from '../components/PostAwardPanels'; // CB:BEGIN post-award-panels
import ProjectCancelModal from '../components/ProjectCancelModal'; // CB:BEGIN project-cancellation
// CB:BEGIN homeowner-notification-preferences-import
import NotificationPreferencesPanel from '../components/NotificationPreferencesPanel';
// CB:END homeowner-notification-preferences-import

/**
 * Homeowner project details page - redesigned for clarity
 * Shows clear next steps, contract preview, and progress tracking
 */
export const HomeownerProjectPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user: clerkUser } = useUser();
  const { getToken } = useAuth();
  const { get, post, put } = useApi();
  const homeownerDisplayName = [clerkUser?.firstName, clerkUser?.lastName].filter(Boolean).join(' ') || clerkUser?.primaryEmailAddress?.emailAddress || 'Homeowner';

  const [loading, setLoading] = useState(true);
  const [projectData, setProjectData] = useState<any>(null);
  const [escrowData, setEscrowData] = useState<any>(null);
  const [gateData, setGateData] = useState<any>(null);
  const [signingData, setSigningData] = useState<any>(null);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false); // CB:BEGIN homeowner-reviews
  const [existingReview, setExistingReview] = useState<any>(null); // CB:BEGIN homeowner-reviews
  const [showCancelModal, setShowCancelModal] = useState(false); // CB:BEGIN project-cancellation
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [milestoneEvidence, setMilestoneEvidence] = useState<Record<string, any[]>>({});
  const [expandedEvidence, setExpandedEvidence] = useState<Record<string, boolean>>({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Form state
  const [roofColor] = useState('');
  const [scheduledStartDate, setScheduledStartDate] = useState('');
  const [colorOptions, setColorOptions] = useState<Array<{ id: string; colorNameDisplay: string; distributorLabel: string | null }>>([]);
  const [selectedColorOptionId, setSelectedColorOptionId] = useState('');

  useEffect(() => {
    if (projectId) {
      loadProjectData();
    }
  }, [projectId]);

  const loadProjectData = async () => {
    try {
      setLoading(true);
      const [project, escrow, gate, signing, colorOpts] = await Promise.all([
        get(`/api/projects/${projectId}`),
        get(`/api/escrow/${projectId}/dashboard`).catch(() => null),
        get(`/api/projects/${projectId}/start-gate`).catch(() => null),
        get(`/api/projects/${projectId}/signing-status`).catch(() => null),
        get(`/api/projects/${projectId}/color-options`).catch(() => null),
      ]);

      setProjectData(project);
      setEscrowData(escrow?.escrowAccount || escrow);
      setGateData(gate);
      setSigningData(signing);
      setColorOptions(colorOpts?.colors ?? []);

      // CB:BEGIN homeowner-review-fetch
      if (project?.status === 'COMPLETED' || project?.status === 'CLOSED') {
        get(`/api/projects/${projectId}/review`).then(r => setExistingReview(r)).catch(() => setExistingReview(null));
      }
      // CB:END homeowner-review-fetch

      // Fetch evidence for all milestones so homeowner can view contractor submissions
      const milestoneKeys = ['M1_START', 'M2_DEMO', 'M3_COMPLETE'];
      const evidenceResults = await Promise.all(
        milestoneKeys.map(key =>
          get(`/api/projects/${projectId}/milestones/${key}/evidence`).catch(() => null)
        )
      );
      const evidenceMap: Record<string, any[]> = {};
      milestoneKeys.forEach((key, i) => {
        evidenceMap[key] = evidenceResults[i]?.evidence ?? [];
      });
      setMilestoneEvidence(evidenceMap);

      setLoading(false);
    } catch (error) {
      console.error('Failed to load project data:', error);
      setLoading(false);
    }
  };

  const handleNavigate = (screen: string) => {
    if (screen === 'customer-dashboard') {
      navigate('/');
    } else if (screen === 'payment-screen') {
      // Set active_project_id so PaymentScreen can find the project (same as CustomerDashboard's "Make payment" path)
      if (projectId) {
        try { localStorage.setItem('active_project_id', projectId); } catch {}
      }
      window.location.href = '/payment';
    } else {
      // For legacy app screens (e.g. escrow-dashboard), use a full navigation
      // so App.tsx initializes currentScreen correctly from the URL.
      window.location.href = `/${screen}`;
    }
  };

  const openContractModal = () => {
    setAgreedToTerms(false); // Reset checkbox when opening modal
    setShowContractModal(true);
  };

  const handleSign = async () => {
    if (!agreedToTerms) {
      alert('Please check the agreement box to continue.');
      return;
    }

    try {
      setActionLoading('sign');
      await post(`/api/projects/${projectId}/sign`, { party: 'HOMEOWNER' });
      await loadProjectData();
      setShowContractModal(false);
      setAgreedToTerms(false); // Reset after signing
    } catch (error) {
      console.error('Failed to sign contract:', error);
      alert('Failed to sign contract. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveColor = async () => {
    if (!selectedColorOptionId) return;
    try {
      setActionLoading('color');
      await put(`/api/projects/${projectId}/roof-color`, { colorOptionId: selectedColorOptionId });
      await loadProjectData();
    } catch (error) {
      console.error('Failed to save roof color:', error);
      alert('Failed to save color. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  // CB:BEGIN start-date-workflow
  const handleProposeStartDate = async () => {
    if (!scheduledStartDate) return;
    try {
      setActionLoading('startDate');
      const preferredStartDate = scheduledStartDate.includes('T') ? scheduledStartDate : `${scheduledStartDate}T12:00:00.000Z`;
      await post(`/api/projects/${projectId}/start-date/propose`, { preferredStartDate });
      await loadProjectData();
      setScheduledStartDate('');
    } catch (error) {
      console.error('Failed to propose start date:', error);
      alert('Failed to propose start date. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAcceptCounter = async () => {
    try {
      setActionLoading('acceptCounter');
      await post(`/api/projects/${projectId}/start-date/confirm-counter`, {});
      await loadProjectData();
    } catch (error) {
      console.error('Failed to confirm counter date:', error);
      alert('Failed to confirm date. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };
  // CB:END start-date-workflow

  const handleStartProject = async () => {
    if (!window.confirm('Are you ready to start this project?')) return;
    try {
      setActionLoading('start');
      await post(`/api/projects/${projectId}/start`, {});
      await loadProjectData();
    } catch (error) {
      console.error('Failed to start project:', error);
      alert('Failed to start project. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const downloadContract = async () => {
    try {
      const apiUrl = getApiUrl(`/api/projects/${projectId}/homeowner-agreement-pdf`);
      const token = await getToken();
      const response = await fetch(apiUrl, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CoreBids_Homeowner_Agreement_${projectId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      alert('Failed to download agreement. Please try again.');
    }
  };

  // Determine what the next step is
  const getNextStep = () => {
    if (!gateData) return null;

    // Short-circuit for completed/closed projects
    if (projectData?.status === 'COMPLETED' || projectData?.status === 'CLOSED') {
      return null;
    }

    const conditions = gateData.conditions || {};

    if (!conditions.contractSigned) {
      if (!signingData?.homeownerSigned) {
        return {
          title: 'Review & Sign Contract',
          description: 'Review and sign the agreement before anything else',
          action: () => openContractModal(),
          actionLabel: 'View & Sign Contract',
          icon: '',
          color: '#8b5cf6'
        };
      } else {
        return {
          title: 'Waiting for Contractor Signature',
          description: 'Your signature is complete. Contractor will sign soon.',
          action: null,
          icon: '',
          color: '#f59e0b'
        };
      }
    }

    if (!conditions.depositCaptured && !conditions.escrowFunded) {
      return {
        title: 'Make Payment',
        description: 'Fund escrow to secure the project',
        action: () => handleNavigate('payment-screen'),
        actionLabel: 'Go to Payment',
        icon: '',
        color: '#3b82f6'
      };
    }

    if (!conditions.colorSelected) {
      return {
        title: 'Choose Roof Color',
        description: 'Select your preferred shingle color',
        action: null, // Inline form below
        icon: '',
        color: '#10b981'
      };
    }

    if (!conditions.scheduledStartSet) {
      return {
        title: 'Set Start Date',
        description: 'Coordinate with contractor to schedule work',
        action: null, // Inline form below
        icon: '',
        color: '#f59e0b'
      };
    }

    // Start date proposed but contractor hasn't confirmed yet
    const sdStatus = gateData.startDateStatus;
    if (sdStatus === 'HOMEOWNER_PROPOSED') {
      return {
        title: 'Awaiting Contractor Confirmation',
        description: `You proposed ${gateData.homeownerPreferredStartDate ? new Date(gateData.homeownerPreferredStartDate).toLocaleDateString('en-US', { month: 'long', d: 'numeric', year: 'numeric' }) : 'a start date'} — waiting for the contractor to accept or suggest a new date.`,
        action: null,
        icon: '',
        color: '#f59e0b'
      };
    }

    if (sdStatus === 'CONTRACTOR_COUNTERED') {
      return {
        title: 'Contractor Proposed a New Date',
        description: `Review the contractor's suggested start date and confirm to proceed.`,
        action: null,
        icon: '',
        color: '#f59e0b'
      };
    }

    if (gateData.passed) {
      return {
        title: 'Project In Progress',
        description: 'Start date confirmed. Track milestones as work progresses.',
        action: null,
        icon: '',
        color: '#10b981'
      };
    }

    return {
      title: 'Ready to Start',
      description: 'All requirements met. Click below to begin work.',
      action: handleStartProject,
      actionLabel: 'Start Project',
      icon: '',
      color: '#10b981'
    };
  };

  const nextStep = getNextStep();
  const conditions = gateData?.conditions || {};

  if (loading) {
    return (
      <ResponsiveContainer>
        <SharedHeaderCustomer onNavigate={handleNavigate} />
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <div className="spinner" />
          <p style={{ marginTop: 16, color: '#6b7280' }}>Loading project details...</p>
        </div>
      </ResponsiveContainer>
    );
  }

  if (!projectId || !projectData) {
    return (
      <ResponsiveContainer>
        <SharedHeaderCustomer onNavigate={handleNavigate} />
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <h2>Project not found</h2>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/')}
            style={{ marginTop: 20 }}
          >
            Back to Dashboard
          </button>
        </div>
      </ResponsiveContainer>
    );
  }

  const progressPercent = [
    conditions.contractSigned,
    conditions.depositCaptured || conditions.escrowFunded,
    conditions.colorSelected,
    conditions.scheduledStartSet,
    gateData?.passed
  ].filter(Boolean).length / 5 * 100;

  return (
    <>
    <ResponsiveContainer>
      <SharedHeaderCustomer onNavigate={handleNavigate} />

      <div style={{ padding: '20px', maxWidth: 1000, margin: '0 auto' }}>
        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'none',
            border: 'none',
            color: '#3b82f6',
            cursor: 'pointer',
            fontSize: 14,
            padding: '8px 0',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 4
          }}
        >
          ← Back to Dashboard
        </button>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
            <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>
              {projectData.projectType || projectData.project_type || 'Roof'} Replacement
            </h1>
            {projectData.status === 'COMPLETED' && (
              <span style={{
                background: '#d1fae5',
                color: '#065f46',
                fontSize: 13,
                fontWeight: 700,
                padding: '4px 12px',
                borderRadius: 20,
                whiteSpace: 'nowrap',
              }}>
                ✓ Project Complete
              </span>
            )}
            {/* CB:BEGIN homeowner-review-button */}
            {(projectData.status === 'COMPLETED' || projectData.status === 'CLOSED') && !existingReview && (
              <button
                type="button"
                style={{
                  background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '6px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
                onClick={() => setShowReviewModal(true)}
              >
                Leave a Review
              </button>
            )}
            {existingReview && (
              <span style={{
                background: '#dbeafe', color: '#1d4ed8', fontSize: 13, fontWeight: 600,
                padding: '4px 12px', borderRadius: 20, whiteSpace: 'nowrap',
              }}>
                ✓ Review submitted
              </span>
            )}
            {/* CB:END homeowner-review-button */}
            {/* CB:BEGIN project-cancel-button */}
            {!['COMPLETED', 'CLOSED', 'CANCELLED', 'ACTIVE', 'ACTIVE_P1', 'ACTIVE_P2', 'ACTIVE_P3', 'IN_PROGRESS'].includes(projectData.status) && (
              <button
                type="button"
                style={{
                  background: 'transparent',
                  color: '#9ca3af',
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  padding: '4px 10px',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
                onClick={() => setShowCancelModal(true)}
              >
                Cancel project
              </button>
            )}
            {projectData.status === 'CANCELLED' && (
              <span style={{
                background: '#fee2e2', color: '#991b1b', fontSize: 13, fontWeight: 600,
                padding: '4px 12px', borderRadius: 20,
              }}>
                Cancelled
              </span>
            )}
            {/* CB:END project-cancel-button */}
          </div>
          <p style={{ color: '#6b7280', fontSize: 16 }}>
            {projectData.address}
          </p>
        </div>

        {/* Progress bar + setup checklist — hidden for completed/closed projects */}
        {projectData.status !== 'COMPLETED' && projectData.status !== 'CLOSED' && (<>
        <div style={{
          background: '#f3f4f6',
          borderRadius: 12,
          padding: 16,
          marginBottom: 24
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
              Project Setup Progress
            </span>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#3b82f6' }}>
              {Math.round(progressPercent)}%
            </span>
          </div>
          <div style={{
            background: '#e5e7eb',
            borderRadius: 8,
            height: 12,
            overflow: 'hidden'
          }}>
            <div style={{
              background: 'linear-gradient(90deg, #3b82f6 0%, #10b981 100%)',
              height: '100%',
              width: `${progressPercent}%`,
              transition: 'width 0.5s ease'
            }} />
          </div>
        </div>

        {/* Next Step Banner */}
        {nextStep && (
          <div style={{
            background: `linear-gradient(135deg, ${nextStep.color}15 0%, ${nextStep.color}25 100%)`,
            border: `2px solid ${nextStep.color}`,
            borderRadius: 12,
            padding: 20,
            marginBottom: 24,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'start', gap: 16, flexWrap: 'wrap' }}>
              {nextStep.icon ? <div style={{ fontSize: 32 }}>{nextStep.icon}</div> : null}
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4, color: '#1f2937' }}>
                  Next Step: {nextStep.title}
                </h3>
                <p style={{ color: '#6b7280', marginBottom: nextStep.action ? 12 : 0 }}>
                  {nextStep.description}
                </p>
                {nextStep.action && nextStep.actionLabel && (
                  <button
                    onClick={nextStep.action}
                    disabled={!!actionLoading}
                    style={{
                      background: nextStep.color,
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      padding: '10px 20px',
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {nextStep.actionLabel} →
                  </button>
                )}
              </div>
              {nextStep.title === 'Project In Progress' && (
                <button
                  onClick={() => {
                    const el = document.getElementById('milestones-section');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  style={{
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 20px',
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Milestones →
                </button>
              )}
            </div>
          </div>
        )}

        {/* Setup Checklist */}
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 24,
          marginBottom: 24
        }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Setup Checklist</h3>

          {/* Step 1: Contract — always active, must be signed first */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: 12,
            borderRadius: 8,
            background: conditions.contractSigned ? '#f0fdf4' : '#fdf4ff',
            border: !conditions.contractSigned ? '2px solid #8b5cf6' : '2px solid transparent',
            marginBottom: 12
          }}>
            <div style={{
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              background: conditions.contractSigned ? '#10b981' : '#8b5cf6',
              color: 'white',
              fontWeight: 600,
              flexShrink: 0
            }}>
              {conditions.contractSigned ? '✓' : '1'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>Sign Contract</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                {signingData?.homeownerSigned && !conditions.contractSigned
                  ? 'Waiting for contractor to countersign'
                  : conditions.contractSigned
                  ? `Signed by ${homeownerDisplayName}`
                  : 'Required before any other steps'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {conditions.contractSigned && (
                <button
                  onClick={async () => {
                    const url = getApiUrl(`/api/projects/${projectId}/homeowner-agreement-pdf`);
                    try {
                      const token = await getToken();
                      const res = await fetch(url, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} });
                      if (res.ok) {
                        const blob = await res.blob();
                        window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
                      }
                    } catch { window.open(url, '_blank', 'noopener,noreferrer'); }
                  }}
                  style={{
                    background: 'white',
                    color: '#8b5cf6',
                    border: '1.5px solid #8b5cf6',
                    borderRadius: 6,
                    padding: '6px 12px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap' as const,
                  }}
                >
                  View Contract
                </button>
              )}
              {!signingData?.homeownerSigned && (
                <button
                  onClick={() => openContractModal()}
                  style={{
                    background: '#8b5cf6',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 12px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  View & Sign
                </button>
              )}
            </div>
          </div>

          {/* Steps 2–4: locked until contract is signed */}
          <div style={{
            opacity: conditions.contractSigned ? 1 : 0.4,
            pointerEvents: conditions.contractSigned ? 'auto' : 'none',
          }}>
            {!conditions.contractSigned && (
              <div style={{ fontSize: 12, color: '#8b5cf6', fontWeight: 500, marginBottom: 8, paddingLeft: 4 }}>
                Sign the contract above to unlock these steps
              </div>
            )}

            {/* Step 2: Payment */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 12,
              borderRadius: 8,
              background: (conditions.depositCaptured || conditions.escrowFunded) ? '#f0fdf4' : '#f9fafb',
              marginBottom: 12
            }}>
              <div style={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                background: (conditions.depositCaptured || conditions.escrowFunded) ? '#10b981' : '#e5e7eb',
                color: 'white',
                fontWeight: 600,
                flexShrink: 0
              }}>
                {(conditions.depositCaptured || conditions.escrowFunded) ? '✓' : '2'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>Payment</div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  ${((escrowData?.totalAmountCents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} escrow funded
                </div>
              </div>
              {!(conditions.depositCaptured || conditions.escrowFunded) && (
                <button
                  onClick={() => handleNavigate('payment-screen')}
                  style={{
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 12px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Pay Now
                </button>
              )}
            </div>

            {/* Step 3: Roof Color */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 12,
              borderRadius: 8,
              background: conditions.colorSelected ? '#f0fdf4' : '#f9fafb',
              marginBottom: 12
            }}>
              <div style={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                background: conditions.colorSelected ? '#10b981' : '#e5e7eb',
                color: 'white',
                fontWeight: 600,
                flexShrink: 0
              }}>
                {conditions.colorSelected ? '✓' : '3'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>Roof Color</div>
                {conditions.colorSelected ? (
                  <div style={{ fontSize: 13, color: '#6b7280' }}>Selected: {roofColor || projectData?.roofColor || 'Color chosen'}</div>
                ) : (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    {colorOptions.length > 0 ? (
                      <select
                        value={selectedColorOptionId}
                        onChange={(e) => setSelectedColorOptionId(e.target.value)}
                        style={{
                          flex: 1,
                          padding: '6px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: 6,
                          fontSize: 14,
                          minWidth: 160
                        }}
                      >
                        <option value="">Select a color…</option>
                        {colorOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.colorNameDisplay}{opt.distributorLabel ? ` — ${opt.distributorLabel}` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div style={{ flex: 1, padding: '6px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, color: '#6b7280', background: '#f9fafb' }}>
                        No color options submitted by contractor yet
                      </div>
                    )}
                    <button
                      onClick={handleSaveColor}
                      disabled={!selectedColorOptionId || !!actionLoading}
                      style={{
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        padding: '6px 16px',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        opacity: !selectedColorOptionId ? 0.5 : 1
                      }}
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Step 4: Start Date — CB:BEGIN start-date-workflow */}
            {(() => {
              const sdStatus = gateData?.startDateStatus ?? 'NO_START_DATE';
              const isConfirmed = sdStatus === 'CONFIRMED';
              const isCountered = sdStatus === 'CONTRACTOR_COUNTERED';
              const isProposed = sdStatus === 'HOMEOWNER_PROPOSED';
              const agreedDate = gateData?.agreedStartDate;
              const counterDate = gateData?.contractorSuggestedStartDate;
              const preferredDate = gateData?.homeownerPreferredStartDate;
              return (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: 12,
                  borderRadius: 8,
                  background: isConfirmed ? '#f0fdf4' : isCountered ? '#fffbeb' : '#f9fafb'
                }}>
                  <div style={{
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    background: isConfirmed ? '#10b981' : isCountered ? '#f59e0b' : '#e5e7eb',
                    color: 'white',
                    fontWeight: 600,
                    flexShrink: 0,
                    marginTop: 2
                  }}>
                    {isConfirmed ? '✓' : '4'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>Start Date</div>

                    {/* CONFIRMED */}
                    {isConfirmed && (
                      <div style={{ fontSize: 13, color: '#065f46', marginTop: 4 }}>
                        Agreed start: <strong>{agreedDate ? new Date(agreedDate).toLocaleDateString() : 'Confirmed'}</strong>
                      </div>
                    )}

                    {/* HOMEOWNER_PROPOSED — awaiting contractor */}
                    {isProposed && (
                      <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                        You proposed <strong>{preferredDate ? new Date(preferredDate).toLocaleDateString() : '—'}</strong> — waiting for contractor to respond.
                      </div>
                    )}

                    {/* CONTRACTOR_COUNTERED — contractor suggested alternate */}
                    {isCountered && (
                      <div style={{ marginTop: 6 }}>
                        <div style={{ fontSize: 13, color: '#92400e', marginBottom: 8 }}>
                          Contractor suggested <strong>{counterDate ? new Date(counterDate).toLocaleDateString() : '—'}</strong> instead.
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            onClick={handleAcceptCounter}
                            disabled={!!actionLoading}
                            style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                          >
                            {actionLoading === 'acceptCounter' ? 'Confirming…' : 'Accept This Date'}
                          </button>
                          <span style={{ fontSize: 12, color: '#9ca3af', alignSelf: 'center' }}>or propose a new date below</span>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <input
                            type="date"
                            value={scheduledStartDate}
                            onChange={(e) => setScheduledStartDate(e.target.value)}
                            style={{ flex: 1, padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                          />
                          <button
                            onClick={handleProposeStartDate}
                            disabled={!scheduledStartDate || !!actionLoading}
                            style={{ background: '#f59e0b', color: 'white', border: 'none', borderRadius: 6, padding: '6px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: !scheduledStartDate ? 0.5 : 1 }}
                          >
                            {actionLoading === 'startDate' ? 'Sending…' : 'Propose'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* NO_START_DATE / CHANGE_REQUESTED — propose new */}
                    {(sdStatus === 'NO_START_DATE' || sdStatus === 'CHANGE_REQUESTED') && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <input
                          type="date"
                          value={scheduledStartDate}
                          onChange={(e) => setScheduledStartDate(e.target.value)}
                          style={{ flex: 1, padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                        />
                        <button
                          onClick={handleProposeStartDate}
                          disabled={!scheduledStartDate || !!actionLoading}
                          style={{ background: '#f59e0b', color: 'white', border: 'none', borderRadius: 6, padding: '6px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: !scheduledStartDate ? 0.5 : 1 }}
                        >
                          {actionLoading === 'startDate' ? 'Sending…' : 'Propose'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
            {/* CB:END start-date-workflow */}
          </div>
        </div>
        </>)} {/* end completed-check */}

        {/* Milestone Progress Tracker */}
        {escrowData?.milestones && escrowData.milestones.length > 0 && (() => {
          const keyConfig: Record<string, { label: string; shortLabel: string; pct: number; desc: string }> = {
            M1_START:    { label: 'Project Start',    shortLabel: 'Start',       pct: 25, desc: 'Work has begun' },
            M2_DEMO:     { label: 'Demolition',       shortLabel: 'Demo',        pct: 25, desc: 'Old materials removed' },
            M3_COMPLETE: { label: 'Completion',       shortLabel: 'Complete',    pct: 50, desc: 'Final inspection done' },
          };
          const isReleased = (s: string) => ['RELEASED', 'ADMIN_RELEASED', 'COMPLETED', 'HOMEOWNER_APPROVED'].includes(String(s).toUpperCase());
          const isActive = (s: string) => ['EVIDENCE_SUBMITTED', 'APPROVED', 'PENDING_APPROVAL'].includes(String(s).toUpperCase());

          // Build ordered steps from the milestones array
          const keyOrder = ['M1_START', 'M2_DEMO', 'M3_COMPLETE'];
          const milestoneMap: Record<string, any> = {};
          for (const m of escrowData.milestones) {
            if (m.milestoneKey) milestoneMap[m.milestoneKey] = m;
          }

          // Calculate progress %
          let progressPct = 0;
          for (const key of keyOrder) {
            const m = milestoneMap[key];
            if (m && isReleased(m.status)) progressPct += keyConfig[key]?.pct ?? 0;
          }
          const projectCompleted = ['COMPLETED', 'CLOSED'].includes(String(projectData?.status));
          const finalPct = projectCompleted ? 100 : progressPct;

          return (
            <div id="milestones-section" style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: 24,
              marginBottom: 24
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Project Progress</h3>
                <span style={{ fontSize: 22, fontWeight: 800, color: finalPct === 100 ? '#10b981' : '#1f2937' }}>{finalPct}%</span>
              </div>

              {/* Progress bar */}
              <div style={{ height: 10, background: '#f3f4f6', borderRadius: 99, marginBottom: 24, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${finalPct}%`,
                  background: finalPct === 100 ? '#10b981' : 'linear-gradient(90deg, #10b981, #3b82f6)',
                  borderRadius: 99,
                  transition: 'width 0.5s ease',
                }} />
              </div>

              {/* Steps */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {keyOrder.map((key, idx) => {
                  const m = milestoneMap[key];
                  const cfg = keyConfig[key];
                  if (!m || !cfg) return null;
                  const released = isReleased(m.status);
                  const active = !released && isActive(m.status);
                  const amountStr = `$${(m.amountCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                  return (
                    <div key={key} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 14,
                      paddingBottom: 20,
                    }}>
                      {/* Icon + connector */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%',
                          background: released ? '#10b981' : active ? '#3b82f6' : '#f3f4f6',
                          border: `2px solid ${released ? '#10b981' : active ? '#3b82f6' : '#d1d5db'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14, fontWeight: 700,
                          color: released || active ? 'white' : '#9ca3af',
                        }}>
                          {released ? '✓' : idx + 1}
                        </div>
                        {idx < keyOrder.length - 1 && (
                          <div style={{ width: 2, flex: 1, minHeight: 16, background: released ? '#10b981' : '#e5e7eb', marginTop: 4 }} />
                        )}
                      </div>
                      {/* Content */}
                      <div style={{ paddingTop: 4, flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600, fontSize: 15, color: released ? '#065f46' : active ? '#1d4ed8' : '#374151' }}>
                            {cfg.label}
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 600,
                            padding: '2px 10px', borderRadius: 99,
                            background: released ? '#d1fae5' : active ? '#dbeafe' : '#f3f4f6',
                            color: released ? '#065f46' : active ? '#1e40af' : '#6b7280',
                          }}>
                            {released ? 'Released' : active ? 'In Review' : 'Pending'}
                          </span>
                        </div>
                        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                          {cfg.pct}% · {amountStr} · {cfg.desc}
                        </div>
                        {/* Evidence viewer */}
                        {(milestoneEvidence[key] ?? []).length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <button
                              onClick={() => setExpandedEvidence(prev => ({ ...prev, [key]: !prev[key] }))}
                              style={{ fontSize: 12, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}
                            >
                              {expandedEvidence[key] ? '▾ Hide evidence' : `▸ View evidence (${milestoneEvidence[key].length})`}
                            </button>
                            {expandedEvidence[key] && (
                              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {milestoneEvidence[key].flatMap((e: any) =>
                                  (e.signedUrls ?? []).map((url: string, i: number) => (
                                    <a key={`${e.id}-${i}`} href={url} target="_blank" rel="noopener noreferrer">
                                      <img
                                        src={url}
                                        alt={`Evidence ${i + 1}`}
                                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb', cursor: 'pointer' }}
                                        onError={(ev) => { (ev.target as HTMLImageElement).style.display = 'none'; }}
                                      />
                                    </a>
                                  ))
                                )}
                                {milestoneEvidence[key].some((e: any) => (e.signedUrls ?? []).length === 0) && (
                                  <span style={{ fontSize: 12, color: '#6b7280' }}>Files submitted (no preview available)</span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Final step: Project Complete */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{ flexShrink: 0 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: projectCompleted ? '#10b981' : '#f3f4f6',
                      border: `2px solid ${projectCompleted ? '#10b981' : '#d1d5db'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 700,
                      color: projectCompleted ? 'white' : '#9ca3af',
                    }}>
                      {projectCompleted ? '✓' : '4'}
                    </div>
                  </div>
                  <div style={{ paddingTop: 4, flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, color: projectCompleted ? '#065f46' : '#374151' }}>
                      Project Complete
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                      {projectCompleted ? 'Your project is finished.' : 'All milestones must be released'}
                    </div>
                    {projectCompleted && !existingReview && (
                      <button
                        onClick={() => setShowReviewModal(true)}
                        style={{
                          marginTop: 10,
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: 8,
                          padding: '8px 18px',
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Leave a Review →
                      </button>
                    )}
                    {existingReview && (
                      <div style={{ marginTop: 8, fontSize: 13, color: '#10b981', fontWeight: 600 }}>
                        ✓ Review submitted — thank you!
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Support contact */}
              <div style={{
                marginTop: 24,
                padding: '16px 20px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
              }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#065f46', marginBottom: 2 }}>
                    Have a question or concern?
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    Our team is here to help at every step.
                  </div>
                </div>
                <a
                  href="mailto:support@corebids.com"
                  style={{
                    display: 'inline-block',
                    background: '#10b981',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: 14,
                    padding: '9px 18px',
                    borderRadius: 8,
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Contact Support
                </a>
              </div>
            </div>
          );
        })()}

        {/* Start Project Button */}
        {!gateData?.passed && progressPercent === 100 && (
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <button
              onClick={handleStartProject}
              disabled={!!actionLoading}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                padding: '16px 40px',
                fontSize: 18,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)'
              }}
            >
              Start Project
            </button>
          </div>
        )}

        {/* Contract Modal */}
        {showContractModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20
          }}>
            <div style={{
              background: 'white',
              borderRadius: 12,
              maxWidth: 800,
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Homeowner Project Agreement</h3>
                <button
                  onClick={() => setShowContractModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 24,
                    cursor: 'pointer',
                    color: '#6b7280',
                    lineHeight: 1,
                    padding: 0,
                    width: 32,
                    height: 32
                  }}
                >
                  ×
                </button>
              </div>

              {/* Contract Content */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: 24
              }}>
                <AgreementViewer agreementType="homeowner-project" maxHeight="none" />
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '16px 24px',
                borderTop: '1px solid #e5e7eb',
                background: '#f9fafb'
              }}>
                {/* Download Button */}
                <div style={{ marginBottom: 16 }}>
                  <button
                    onClick={downloadContract}
                    style={{
                      background: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: 8,
                      padding: '10px 20px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      color: '#374151'
                    }}
                  >
                    Download Agreement
                  </button>
                </div>

                {/* Agreement Checkbox and Sign Button */}
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                    />
                    <span style={{ fontSize: 14 }}>I have read and agree to the agreement</span>
                  </label>
                  <button
                    onClick={handleSign}
                    disabled={!!actionLoading || !agreedToTerms}
                    style={{
                      background: agreedToTerms && !actionLoading ? '#8b5cf6' : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      padding: '10px 24px',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {actionLoading === 'sign' ? 'Signing...' : 'I Agree & Sign'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ResponsiveContainer>

      {/* CB:BEGIN post-award-section */}
      {projectId && projectData?.status && (
        <PostAwardSection
          projectId={projectId}
          role="homeowner"
          projectStatus={projectData.status}
          isInsuranceClaim={!!projectData.insuranceClaim}
          onProjectUpdated={loadProjectData}
        />
      )}
      {/* CB:END post-award-section */}

      {/* CB:BEGIN homeowner-review-modal */}
      {showReviewModal && projectId && (
        <ReviewModal
          projectId={projectId}
          contractorName={projectData?.contractor?.company || 'Your Contractor'}
          onClose={() => setShowReviewModal(false)}
          onSubmitted={() => {
            setShowReviewModal(false);
            loadProjectData();
          }}
        />
      )}
      {/* CB:END homeowner-review-modal */}
      {/* CB:BEGIN project-cancel-modal */}
      {showCancelModal && projectId && (
        <ProjectCancelModal
          projectId={projectId}
          projectAddress={projectData?.address}
          onClose={() => setShowCancelModal(false)}
          onCancelled={() => {
            setShowCancelModal(false);
            // Reload project data to reflect CANCELLED status
            if (typeof (window as any).loadProjectData === 'function') {
              (window as any).loadProjectData();
            } else {
              window.location.reload();
            }
          }}
        />
      )}
      {/* CB:END project-cancel-modal */}
      {/* CB:BEGIN homeowner-notification-preferences */}
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '0 16px 24px' }}>
        <NotificationPreferencesPanel role="HOMEOWNER" />
      </div>
      {/* CB:END homeowner-notification-preferences */}
    </>
  );
};

export default HomeownerProjectPage;
